import React, {
  ComponentProps,
  ComponentType,
  ReactElement,
  ReactNode,
  RefCallback,
  cloneElement,
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@oneleet/utils";

import { motion } from "framer-motion";

interface NgHideoutContextProps {
  isExpanded: boolean;
  toggle: () => void;
  showCount: number;
  totalItems: number;
  peekFirstHiddenItem?: boolean;
  hiddenItemsCount: number;
}

const NgHideoutContext = createContext<NgHideoutContextProps>({
  isExpanded: false,
  toggle: () => {},
  showCount: 0,
  totalItems: 0,
  peekFirstHiddenItem: false,
  hiddenItemsCount: 0,
});

export interface NgHideoutItemProps {
  children: ReactNode;
  className?: string;
}

const NgHideoutItem = ({
  children,
  className,
  ...rest
}: NgHideoutItemProps & ComponentProps<"div">) => {
  return (
    <div className={cn("", className)} data-ng-hideout-item {...rest}>
      {children}
    </div>
  );
};
NgHideoutItem.displayName = "NgHideoutItem";

export interface NgHideoutTriggerProps {
  children: (props: {
    isExpanded: boolean;
    hiddenItemsCount: number;
  }) => ReactElement;
  className?: string;
}

/**
 * The NgHideoutTrigger always renders as the direct child without any wrapper.
 */
const NgHideoutTrigger = <T extends ComponentType>({
  children,
  className,
  ...rest
}: ComponentProps<T> & NgHideoutTriggerProps) => {
  const { isExpanded, toggle, hiddenItemsCount, peekFirstHiddenItem } =
    useContext(NgHideoutContext);

  return cloneElement(children({ isExpanded, hiddenItemsCount }), {
    onClick: toggle,
    className: cn(peekFirstHiddenItem && "absolute z-10", className),
    ...rest,
  });
};
NgHideoutTrigger.displayName = "NgHideoutTrigger";

const NgHideoutListGradient = ({
  className,
  ...rest
}: ComponentProps<"div">) => {
  return (
    <div
      {...rest}
      className={cn(
        "pointer-events-none absolute bottom-0 left-0 h-12 w-full bg-gradient-to-t from-white to-transparent",
        className,
      )}
    />
  );
};
NgHideoutListGradient.displayName = "NgHideoutListGradient";

export interface NgHideoutListProps {
  showCount: number;
  children: ReactNode;
  className?: string;
  peekFirstHiddenItem?: boolean;
  expandedOptions?: {
    /**
     * Additional height to be added to the full height of the list when it's expanded.
     * Helps properly animate the list when it's expanded/collapsed when peeking at the first hidden item (uses an absolutely positioned trigger).
     * Attempting to use padding through the className prop does not work as expected with the motion animation.
     */
    additionalHeight?: number;
  };
}

const NgHideoutList = ({
  showCount,
  children,
  className,
  expandedOptions = {},
  peekFirstHiddenItem = true,
}: NgHideoutListProps) => {
  type U = ReactElement<NgHideoutTriggerProps> & {
    ref: RefCallback<HTMLDivElement>;
    offsetHeight: number;
    className?: string;
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const toggle = () => setIsExpanded((prev) => !prev);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [lastVisible, setLastVisible] = useState<HTMLDivElement | null>(null);
  const [actualShowCount, setActualShowCount] = useState(showCount);
  const firstHiddenItemRef = useRef<HTMLDivElement>(null);

  const childrenArray = React.Children.toArray(children);

  const triggerChild = childrenArray.find((child) => {
    const element = child as React.ReactElement;
    return (
      element.type &&
      typeof element.type !== "string" &&
      "displayName" in element.type &&
      element.type.displayName === "NgHideoutTrigger"
    );
  }) as ReactElement<U>;

  const gradientChild = childrenArray.find((child) => {
    const element = child as React.ReactElement;
    return (
      element.type &&
      typeof element.type !== "string" &&
      "displayName" in element.type &&
      element.type.displayName === "NgHideoutListGradient"
    );
  });

  const itemChildren = childrenArray.filter((child) => {
    const element = child as React.ReactElement;
    return (
      element.type &&
      typeof element.type !== "string" &&
      "displayName" in element.type &&
      element.type.displayName === "NgHideoutItem"
    );
  });

  const totalItems = itemChildren.length;

  useLayoutEffect(() => {
    if (!container || !itemChildren.length) return;

    const items = Array.from(container.children).filter((child) =>
      (child as HTMLElement).hasAttribute("data-ng-hideout-item"),
    ) as HTMLElement[];

    if (!items.length) return;

    const containerRect = container.getBoundingClientRect();

    const containerTop = containerRect.top;
    const maxHeight =
      items[showCount - 1]?.getBoundingClientRect().bottom - containerTop;
    if (!maxHeight) return;

    let newShowCount = showCount;
    for (let i = showCount; i < items.length; i++) {
      const itemRect = items[i]?.getBoundingClientRect();
      if (!itemRect) continue;

      const visibleHeight = itemRect.bottom - containerTop;
      if (visibleHeight > maxHeight) {
        break;
      }
      newShowCount++;
    }

    if (newShowCount > showCount) {
      setActualShowCount(newShowCount);
    }
  }, [container, itemChildren.length, showCount]);

  const visibleItems = itemChildren.slice(0, actualShowCount);
  const hiddenItems = itemChildren.slice(actualShowCount);

  const visibleItemsWithRef = visibleItems.map((child, index) => {
    if (index === visibleItems.length - 1) {
      return React.cloneElement(
        child as ReactElement<ComponentProps<"div">> & {
          ref?: RefCallback<HTMLDivElement>;
        },
        {
          ref: (element) => {
            if (element) setLastVisible(element);
          },
        },
      );
    }
    return child;
  });

  const hiddenItemsWithRef = hiddenItems.map((child, index) => {
    return React.cloneElement(child as ReactElement<NgHideoutItemProps>, {
      ...(index === 0 ? { ref: firstHiddenItemRef } : {}),
      className: cn(
        (child as ReactElement<NgHideoutItemProps>).props.className,
        !isExpanded && "pointer-events-none",
      ),
    });
  });

  const [fullHeight, setFullHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  const isAlreadyShowingFullContent = actualShowCount >= totalItems;

  useLayoutEffect(() => {
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    let baseCollapsedHeight = 0;
    if (lastVisible) {
      const lastVisibleRect = lastVisible.getBoundingClientRect();
      baseCollapsedHeight = lastVisibleRect.bottom - containerRect.top;
    }

    let peekHeight = 0;
    if (
      peekFirstHiddenItem &&
      hiddenItems.length > 0 &&
      firstHiddenItemRef.current
    ) {
      const firstHiddenHeight = firstHiddenItemRef.current.offsetHeight;
      peekHeight = Math.round(firstHiddenHeight);
    }

    let fullHeight = container.scrollHeight;
    if (isExpanded && expandedOptions.additionalHeight) {
      fullHeight += expandedOptions.additionalHeight;
    }

    setCollapsedHeight(baseCollapsedHeight + peekHeight);
    setFullHeight(fullHeight);
  }, [
    container,
    lastVisible,
    hiddenItems.length,
    peekFirstHiddenItem,
    isExpanded,
    expandedOptions.additionalHeight,
  ]);

  return (
    <NgHideoutContext.Provider
      value={{
        isExpanded,
        toggle,
        showCount,
        totalItems,
        peekFirstHiddenItem,
        hiddenItemsCount: Math.max(totalItems - actualShowCount, 0),
      }}
    >
      <div className="relative">
        <motion.div
          ref={(element) => {
            if (element) setContainer(element);
          }}
          className={cn("content-start overflow-hidden", className)}
          initial={{ height: collapsedHeight }}
          animate={{
            height:
              isExpanded || isAlreadyShowingFullContent
                ? fullHeight
                : collapsedHeight,
          }}
          transition={{ duration: 0.3 }}
        >
          {visibleItemsWithRef}
          {hiddenItemsWithRef}
          {!isExpanded && !isAlreadyShowingFullContent && gradientChild}
        </motion.div>
        {isAlreadyShowingFullContent ? null : triggerChild}
      </div>
    </NgHideoutContext.Provider>
  );
};
NgHideoutList.displayName = "NgHideoutList";

/**
 * The NgHideoutList component is a wrapper around the NgHideoutList component.
 * It provides a more convenient API for using the NgHideoutList component.
 *
 * @example
 * ```jsx
 * <NgHideoutList showCount={2}>
 *   <NgHideoutList.Trigger>
 *     {({ isExpanded, hiddenItemsCount }) =>
 *       isExpanded ? (<NgButton>Show less</NgButton>) : (<NgButton>Show {hiddenItemsCount} more</NgButton>)
 *     }
 *   </NgHideoutList.Trigger>
 *   <NgHideoutList.Item >
 *     Item 1
 *   </NgHideoutList.Item>
 *   <NgHideoutList.Item>
 *     Item 2
 *   </NgHideoutList.Item>
 *   <NgHideoutList.Item>
 *     Item 3
 *   </NgHideoutList.Item>
 *   <NgHideoutList.Gradient />
 * </NgHideoutList>
 * ```
 */
const CombinedNgHideoutList = Object.assign(NgHideoutList, {
  Trigger: NgHideoutTrigger,
  Item: NgHideoutItem,
  Gradient: NgHideoutListGradient,
});

export { CombinedNgHideoutList as NgHideoutList };
